import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Row, Col, Form, Select, DatePicker, Checkbox, Card, Alert, message, Spin, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import ExcelJS from 'exceljs';
import { getCompanies, getBranchOffices, getBlocks, getProjects, getCostsCenters, getAreas, getJobPositions } from '../../../../services/general';
import { admissionService } from '../../../../services/admission';
import moment from 'moment';
import evaluationTypes from '../../../../utils/evaluationType.json';
const { Option } = Select;
const MassiveChargeStep1 = ({ form, setCurrentStep, fieldsToInclude, setBaseData }) => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [branchOffices, setBranchOffices] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [companyProjects, setCompanyProjects] = useState([]);
  const [companyAreas, setCompanyAreas] = useState([]);
  const [companyJobPositions, setCompanyJobPositions] = useState([]);
  const [companyCostCenters, setCompanyCostCenters] = useState([]);
  const [loading, setLoading] = useState({
    companies: false,
    branchOffices: false,
    batteries: false,
    projects: false,
    costCenters: false,
  });
  const [dailyCapacity, setDailyCapacity] = useState([]);
  const branchOfficeId = Form.useWatch('branch_office_id', form);
  const companyId = Form.useWatch('company_id', form);
  const outsourcingId = Form.useWatch('outsourcing_id', form);
  const thirdCompanyId = Form.useWatch('third_company_id', form);
  const invoiceCompanyOptions = useMemo(() => {
    const options = [];
    if (companyId) {
      const found = companies.find(company => company.id === companyId);
      if (found) {
        options.push({
          id: companyId,
          name: found.name || found.business_name,
        });
      }
    }
    if (outsourcingId) {
      const found = companies.find(company => company.id === outsourcingId);
      if (found) {
        options.push({
          id: outsourcingId,
          name: found.name || found.business_name,
        });
      }
    }
    if (thirdCompanyId) {
      const found = companies.find(company => company.id === thirdCompanyId);
      if (found) {
        options.push({
          id: thirdCompanyId,
          name: found.name || found.business_name,
        });
      }
    }
    return options;
  }, [companies, companyId, outsourcingId, thirdCompanyId]);
  const fetchBranchOffices = useCallback(async () => {
    setLoading(prev => ({ ...prev, branchOffices: true }));
    try {
      const response = await getBranchOffices();
      setBranchOffices(response.data || response || []);
    } catch (error) {
      console.error('Error fetching branch offices:', error);
      message.error(t('reservations.massiveCharge.step1ErrorBranchOffices'));
    } finally {
      setLoading(prev => ({ ...prev, branchOffices: false }));
    }
  }, [t]);
  const fetchCompanies = useMemo(() => debounce(async (search = '') => {
    setLoading(prev => ({ ...prev, companies: true }));
    try {
      const response = await getCompanies({ query: search, limit: 50 });
      const results = response.data || [];
      setCompanies(prevCompanies => {
        const companyId = form.getFieldValue('company_id');
        const outsourcingId = form.getFieldValue('outsourcing_id');
        const thirdCompanyId = form.getFieldValue('third_company_id');
        const invoiceCompanyId = form.getFieldValue('invoice_company_id');
        const selectedCompanyIds = [companyId, outsourcingId, thirdCompanyId, invoiceCompanyId].filter(Boolean);
        const finalResults = [...results];
        selectedCompanyIds.forEach(id => {
          if (!finalResults.find(company => company.id === id)) {
            const found = prevCompanies.find(company => company.id === id);
            if (found) finalResults.unshift(found);
          }
        });
        return finalResults;
      });
    } catch (error) {
      console.error('Error fetching companies:', error);
      message.error(t('reservations.massiveCharge.step1ErrorCompanies'));
    } finally {
      setLoading(prev => ({ ...prev, companies: false }));
    }
  }, 500), [form, t]);
  const fetchProtocols = useCallback(async (companyId, branchId) => {
    if (!companyId) return;
    const resolvedBranchId = branchId || form.getFieldValue('branch_office_id');
    if (!resolvedBranchId) {
      setBatteries([]);
      return;
    }
    setLoading(prev => ({ ...prev, batteries: true }));
    try {
      const fetchParams = {
        query: '',
        company_id: companyId,
        limit: 100,
        exclude_exams: true,
        filter_batteries_by_company: true,
        branch_office_id: resolvedBranchId,
      };
      const response = await admissionService.getServices(fetchParams);
      setBatteries(response.data || response || []);
    } catch (error) {
      console.error('Error fetching protocols:', error);
    } finally {
      setLoading(prev => ({ ...prev, batteries: false }));
    }
  }, [form]);
  const fetchCompanyProjects = useCallback(async (companyId) => {
    if (!companyId) return;
    setLoading(prev => ({ ...prev, projects: true }));
    try {
      const response = await getProjects({ company_id: companyId, limit: 100 });
      setCompanyProjects(response.data || response || []);
    } catch (error) {
      console.error('Error fetching company projects:', error);
    } finally {
      setLoading(prev => ({ ...prev, projects: false }));
    }
  }, []);
  const fetchCompanyCostCenters = useCallback(async (companyId) => {
    if (!companyId) return;
    setLoading(prev => ({ ...prev, costCenters: true }));
    try {
      const response = await getCostsCenters({ companyId, limit: 100 });
      setCompanyCostCenters(response.data || response || []);
    } catch (error) {
      console.error('Error fetching company cost centers:', error);
    } finally {
      setLoading(prev => ({ ...prev, costCenters: false }));
    }
  }, []);
  const fetchCompanyAreas = useCallback(async (companyId) => {
    if (!companyId) return;
    try {
      const response = await getAreas({ company_id: companyId, limit: 100 });
      setCompanyAreas(response.data || response || []);
    } catch (error) {
      console.error('Error fetching company areas:', error);
    }
  }, []);
  const fetchCompanyJobPositions = useCallback(async (companyId) => {
    if (!companyId) return;
    try {
      const response = await getJobPositions({ company_id: companyId, limit: 1000 });
      setCompanyJobPositions(response.data || response || []);
    } catch (error) {
      console.error('Error fetching company job positions:', error);
    }
  }, []);
  useEffect(() => {
    fetchBranchOffices();
    fetchCompanies();
    const mandatoryFields = fieldsToInclude.filter(f => f.mandatory).map(f => f.key);
    form.setFieldsValue({ fields: mandatoryFields });
    const initialCompanyId = form.getFieldValue('company_id');
    if (initialCompanyId) {
      fetchProtocols(initialCompanyId);
      fetchCompanyProjects(initialCompanyId);
      fetchCompanyCostCenters(initialCompanyId);
      fetchCompanyAreas(initialCompanyId);
      fetchCompanyJobPositions(initialCompanyId);
    }
  }, [fetchBranchOffices, fetchCompanies, fetchProtocols, fetchCompanyProjects, fetchCompanyCostCenters, fetchCompanyAreas, fetchCompanyJobPositions, form, fieldsToInclude]);
  const onFormValuesChange = (changedValues) => {
    if (changedValues.company_id || changedValues.branch_office_id) {
      const companyId = form.getFieldValue('company_id');
      if (companyId) {
        fetchProtocols(companyId, form.getFieldValue('branch_office_id'));
      }
    }
    if (changedValues.company_id) {
      fetchCompanyProjects(changedValues.company_id);
      fetchCompanyCostCenters(changedValues.company_id);
      fetchCompanyAreas(changedValues.company_id);
      fetchCompanyJobPositions(changedValues.company_id);
      
      const currentInvoiceCompanyId = form.getFieldValue('invoice_company_id');
      if (!currentInvoiceCompanyId) {
        form.setFieldsValue({ invoice_company_id: changedValues.company_id });
      }
    }

    const currentInvoiceCompanyId = form.getFieldValue('invoice_company_id');
    if (currentInvoiceCompanyId) {
      const selectedCompanyIds = [
        form.getFieldValue('company_id'),
        form.getFieldValue('outsourcing_id'),
        form.getFieldValue('third_company_id')
      ].filter(Boolean);

      if (!selectedCompanyIds.includes(currentInvoiceCompanyId)) {
        form.setFieldsValue({ invoice_company_id: undefined });
      }
    }
  };
  const dateRange = Form.useWatch('date_range', form);
  const selectedBranchName = useMemo(() => {
    return branchOffices.find(b => b.id === branchOfficeId)?.name || '';
  }, [branchOfficeId, branchOffices]);
  const fetchCapacityData = useCallback(async (branchId, dates, isMounted) => {
    if (!branchId || !dates || dates.length === 0) {
      if (isMounted && isMounted.current) setDailyCapacity([]);
      return;
    }
    if (isMounted && isMounted.current) setLoading(prev => ({ ...prev, branchOffices: true }));
    try {
      const [start, end] = dates;
      const daysCount = moment(end).diff(moment(start), 'days') + 1;
      const displayDays = Math.min(daysCount, 14);
      const daysArray = Array.from({ length: displayDays }, (_, i) => moment(start).add(i, 'days'));
      const results = await Promise.all(daysArray.map(async (currentDate) => {
        try {
          const response = await getBlocks({
            branchOffice: branchId,
            date: currentDate.format('YYYY-MM-DD'),
            department: 1,
          });
          let available = 0;
          if (response && response.blocks) {
            available = response.blocks.reduce((acc, block) => {
              const occupied = block.occupied || block.reservations_count || block.count || 0;
              return acc + Math.max(0, (block.max_capacity || 0) - occupied);
            }, 0);
          }
          return {
            date: currentDate.format('DD/MM/YYYY'),
            available,
          };
        } catch (err) {
          console.error(`Error fetching capacity for ${currentDate.format('DD/MM/YYYY')}:`, err);
          return { date: currentDate.format('DD/MM/YYYY'), available: 0 };
        }
      }));
      if (isMounted && isMounted.current) setDailyCapacity(results);
    } catch (error) {
      console.error('Error fetching capacity range:', error);
    } finally {
      if (isMounted && isMounted.current) setLoading(prev => ({ ...prev, branchOffices: false }));
    }
  }, []);
  useEffect(() => {
    const isMounted = { current: true };
    fetchCapacityData(branchOfficeId, dateRange, isMounted);
    return () => { isMounted.current = false; };
  }, [branchOfficeId, dateRange, fetchCapacityData]);
  const handleDownloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('reservations.massiveCharge.step1TemplateSheetName'));
      const selectedFields = form.getFieldValue('fields') || [];
      const columns = [];
      fieldsToInclude.filter(f => selectedFields.includes(f.key)).forEach(f => {
        if (f.key === 'protocol') {
          [1, 2, 3].forEach(num => {
            columns.push({
              header: `${t('reservations.massiveCharge.step1ProtocolHeader')} ${num}`,
              key: `protocol_${num}`,
              width: 25,
            });
          });
        } else {
          columns.push({
            header: t(`reservations.massiveCharge.fields.${f.key}`),
            key: f.key,
            width: 25,
            style: f.key === 'reservationDate' ? { numFmt: 'dd/mm/yyyy' } : {}
          });
        }
      });
      worksheet.columns = columns;
      worksheet.getRow(1).font = { bold: true };
      const validationWorksheetName = '_validation';
      let validationWorksheet = workbook.getWorksheet(validationWorksheetName);
      if (!validationWorksheet) {
        validationWorksheet = workbook.addWorksheet(validationWorksheetName);
      }
      validationWorksheet.state = 'veryHidden';
      validationWorksheet.columns = [];
      const normalizeValidationElements = (elements) => (
        [...new Set(
          elements
            .filter(Boolean)
            .map(element => String(element).trim())
            .filter(Boolean)
        )]
      );
      const buildValidationRangeFormula = (elements) => {
        const normalizedElements = normalizeValidationElements(elements);
        if (normalizedElements.length === 0) {
          return null;
        }
        const listColumnIndex = validationWorksheet.columnCount + 1;
        const listColumn = validationWorksheet.getColumn(listColumnIndex);
        normalizedElements.forEach((value, index) => {
          validationWorksheet.getCell(index + 1, listColumnIndex).value = value;
        });
        const escapedSheetName = validationWorksheet.name.replace(/'/g, "''");
        return `'${escapedSheetName}'!$${listColumn.letter}$1:$${listColumn.letter}$${normalizedElements.length}`;
      };
      const applyValidationFormulaToColumn = (colIndex, formula) => {
        if (colIndex > 0 && formula) {
          for (let i = 2; i <= 251; i++) {
            worksheet.getCell(i, colIndex).dataValidation = {
              type: 'list',
              allowBlank: true,
              formulae: [formula],
            };
          }
        }
      };
      if (selectedFields.includes('protocol')) {
        const protocolNames = batteries.map(b => b.name);
        const protocolFormula = buildValidationRangeFormula(protocolNames);
        if (protocolFormula) {
          [1, 2, 3].forEach(num => {
            const colIndex = columns.findIndex(c => c.key === `protocol_${num}`) + 1;
            applyValidationFormulaToColumn(colIndex, protocolFormula);
          });
        }
      }
      if (selectedFields.includes('documentType')) {
        const colIndex = columns.findIndex(c => c.key === 'documentType') + 1;
        const documentTypeFormula = buildValidationRangeFormula(['DNI', 'Pasaporte', 'Carnet de Extranjería']);
        applyValidationFormulaToColumn(colIndex, documentTypeFormula);
      }
      const applyDropdownValidation = (fieldKey, elements) => {
        if (selectedFields.includes(fieldKey)) {
          const colIndex = columns.findIndex(c => c.key === fieldKey) + 1;
          const validationFormula = buildValidationRangeFormula(elements);
          applyValidationFormulaToColumn(colIndex, validationFormula);
        }
      };
      if (selectedFields.includes('evaluationType')) {
        applyDropdownValidation('evaluationType', evaluationTypes.PE.map(e => e.text));
      }
      if (selectedFields.includes('gender')) {
        applyDropdownValidation('gender', ['Masculino', 'Femenino']);
      }
      if (selectedFields.includes('project')) {
        applyDropdownValidation('project', companyProjects.map(p => p.name).filter(Boolean));
      }
      if (selectedFields.includes('costCenter')) {
        applyDropdownValidation('costCenter', companyCostCenters.map(c => c.name || c.business_name).filter(Boolean));
      }
      if (selectedFields.includes('area')) {
        applyDropdownValidation('area', companyAreas.map(a => a.name).filter(Boolean));
      }
      if (selectedFields.includes('jobPosition')) {
        applyDropdownValidation('jobPosition', companyJobPositions.map(j => j.name).filter(Boolean));
      }
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('reservations.massiveCharge.step1TemplateFileName')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating template:', error);
      message.error(t('reservations.massiveCharge.step1ErrorTemplate'));
    }
  };
  const handleNextStep = async () => {
    try {
      const values = await form.validateFields();
      if (setBaseData) setBaseData(values);
      setCurrentStep(1);
    } catch (error) {
      console.warn('Validation failed:', error);
    }
  };
  return (
    <div className="massive-charge-step1">
      <Row justify="center">
        <Col span={24}>
          <h3 className="massive-charge-step-title">{t('reservations.massiveCharge.step1Title')}</h3>
          <p className="massive-charge-step-description">{t('reservations.massiveCharge.step1Description')}</p>
        </Col>
      </Row>
      <Form form={form} layout="vertical" size="large" onValuesChange={onFormValuesChange}>
        <Row gutter={15}>
          <Col lg={8} xs={24}>
            <Form.Item name="company_id" label={t('reservations.massiveCharge.step1CompanyLabel')} rules={[{ required: true, message: t('reservations.massiveCharge.step1CompanyRule') }]}>
              <Select
                className="cmt-select"
                placeholder={t('reservations.massiveCharge.step1CompanyPlaceholder')}
                showSearch
                onSearch={fetchCompanies}
                filterOption={false}
                allowClear
                loading={loading.companies}
              >
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name || company.business_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={8} xs={24}>
            <Form.Item name="outsourcing_id" label={t('reservations.massiveCharge.step1OutsourcingLabel')}>
              <Select
                className="cmt-select"
                placeholder={t('reservations.massiveCharge.step1OutsourcingPlaceholder')}
                showSearch
                onSearch={fetchCompanies}
                filterOption={false}
                allowClear
                loading={loading.companies}
                disabled={!form.getFieldValue('company_id')}
              >
                {companies.map((child) => (
                  <Option key={child.id} value={child.id}>
                    {child.name || child.business_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={8} xs={24}>
            <Form.Item name="third_company_id" label={t('reservations.massiveCharge.step1ThirdCompanyLabel')}>
              <Select
                className="cmt-select"
                placeholder={t('reservations.massiveCharge.step1ThirdCompanyPlaceholder')}
                showSearch
                onSearch={fetchCompanies}
                filterOption={false}
                allowClear
                loading={loading.companies}
                disabled={!form.getFieldValue('company_id')}
              >
                {companies.map((third) => (
                  <Option key={third.id} value={third.id}>
                    {third.name || third.business_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={8} xs={24}>
            <Form.Item
              name="invoice_company_id"
              label={t('reservations.generalDataForm.invoiceCompanyLabel')}
              rules={[{ required: true, message: t('reservations.validationButtons.errorInvoiceCompany') }]}
            >
              <Select
                className="cmt-select"
                placeholder={t('reservations.generalDataForm.invoiceCompanyPlaceholder')}
                allowClear
                disabled={!companyId}
              >
                {invoiceCompanyOptions.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={8} xs={24} className="general-data branch-office select-wrapper">
            <Form.Item name="branch_office_id" label={t('reservations.massiveCharge.step1BranchOfficeLabel')} rules={[{ required: true, message: t('reservations.massiveCharge.step1BranchOfficeRule') }]}>
              <Select className="cmt-select" placeholder={t('reservations.massiveCharge.step1BranchOfficePlaceholder')} allowClear loading={loading.branchOffices}>
                {branchOffices.map((branch) => (
                  <Option key={branch.id} value={branch.id}>
                    {branch.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={8} xs={24}>
            <Form.Item name="date_range" label={t('reservations.massiveCharge.step1DateRangeLabel')} rules={[{ required: true, message: t('reservations.massiveCharge.step1DateRangeRule') }]}>
              <DatePicker.RangePicker className="cmt-date-picker" format="DD-MM-YYYY" />
            </Form.Item>
          </Col>
        </Row>
        <Spin spinning={loading.branchOffices}>
          <Row className="mb-15">
            <Col span={24}>
              <div className="massive-charge-capacity-alert">
                {!branchOfficeId || !form.getFieldValue('date_range') || dailyCapacity.length === 0 ? (
                  <span>{t('reservations.massiveCharge.step1SelectBranchDate')}</span>
                ) : (
                  <>
                    <p className="mb-10">
                      {t('reservations.massiveCharge.step1AvailableCapacity')} <strong>{selectedBranchName}</strong>:
                    </p>
                    <Row gutter={[16, 8]}>
                      {dailyCapacity.map((day, idx) => (
                        <Col key={idx} lg={6} md={8} sm={12} xs={24}>
                          <div className="capacity-day-item">
                            <span className="day-date">{day.date}:</span>
                            <span className={`day-slots ${day.available > 0 ? 'available' : 'full'}`}>
                              <strong>{day.available} {t('reservations.massiveCharge.step1Slots')}</strong>
                            </span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Spin>
        {form.getFieldValue('company_id') && (
          <Row className="mb-20">
            <Col span={24}>
              <Alert
                message={t('reservations.massiveCharge.step1ProtocolsAlert')}
                type="info"
                icon={<span className="icon-info-rounded" />}
                showIcon
              />
            </Col>
          </Row>
        )}
        <Card className="massive-charge-fields-card mt-20">
          <Form.Item name="fields" className="mb-0">
            <Checkbox.Group className="massive-charge-checkbox-group">
              <Row gutter={[15, 15]}>
                {fieldsToInclude.map((field) => (
                  <Col lg={6} md={8} sm={12} xs={24} key={field.key}>
                    <Card className={`field-card ${field.mandatory ? 'field-card-disabled' : ''}`} size="small" bordered={true}>
                      <div className="field-card-content">
                        <Checkbox value={field.key} disabled={field.mandatory} className="massive-charge-checkbox">
                          <span className={`field-card-text ${field.mandatory ? 'text-disabled' : ''}`}>
                            {t(`reservations.massiveCharge.fields.${field.key}`)}
                          </span>
                        </Checkbox>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Card>
        <Row justify="space-between" className="mt-30">
          <Col>
            <Button
              className="cmt-button secondary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              loading={loading.batteries}
              disabled={!form.getFieldValue('company_id')}
            >
              {t('reservations.massiveCharge.step1DownloadTemplate')}
            </Button>
          </Col>
          <Col>
            <Button type="primary" className="cmt-button primary" size="large" onClick={handleNextStep}>
              {t('reservations.massiveCharge.step1Next')}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};
export default MassiveChargeStep1;
